package models

import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.KSerializer
import kotlinx.serialization.SealedSerializationApi
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.builtins.nullable
import kotlinx.serialization.builtins.serializer
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder

/**
 * GitLive 2.1's JS encoder sizes arrays from the descriptor's elementsCount, not the size passed
 * to beginCollection. A normal list descriptor has one element even for an empty list, producing
 * [null]. Keep the wire format as an array and tolerate that legacy empty-list representation.
 */
@OptIn(ExperimentalSerializationApi::class, SealedSerializationApi::class)
abstract class GameRecapListSerializer<T : Any>(
    private val element: KSerializer<T>,
) : KSerializer<List<T>> {
    private val nullableList = ListSerializer(element.nullable)
    override val descriptor: SerialDescriptor = ListSerializer(element).descriptor

    override fun serialize(encoder: Encoder, value: List<T>) {
        val sizedDescriptor = object : SerialDescriptor by descriptor {
            override val elementsCount: Int = value.size
        }
        val collection = encoder.beginCollection(sizedDescriptor, value.size)
        value.forEachIndexed { index, item ->
            collection.encodeSerializableElement(sizedDescriptor, index, element, item)
        }
        collection.endStructure(sizedDescriptor)
    }

    override fun deserialize(decoder: Decoder): List<T> =
        nullableList.deserialize(decoder).filterNotNull()
}

object GameRecapCountsSerializer : GameRecapListSerializer<GameRecapPlayerCount>(
    GameRecapPlayerCount.serializer(),
)

object GameRecapPlayerStatsListSerializer : GameRecapListSerializer<PlayerGameStats>(
    PlayerGameStats.serializer(),
)

object GameRecapRoundsListSerializer : GameRecapListSerializer<RoundRecap>(
    RoundRecap.serializer(),
)

object GameRecapIntListSerializer : GameRecapListSerializer<Int>(Int.serializer())

object GameRecapScoreChangesSerializer : GameRecapListSerializer<GameRecapPlayerPoints>(
    GameRecapPlayerPoints.serializer(),
)
